import {
  Box,
  Button,
  Divider,
  Grid,
  InputBase,
  Modal,
  Radio,
  Slide,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  IconWrapper,
  ImageWrapper,
  InputField,
  IStyles,
  PrimaryButton,
  pxToRem,
  pxTovW,
  SelectMenu,
  theme,
} from '@geneo2-web/shared-ui';
import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { ProjectContentCard } from './ProjectContentCard';

const styles: IStyles = {
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100vw',
    marginY: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
    boxSizing: 'border-box',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    gap: { xs: pxToRem(40), md: pxToRem(18), lg: pxTovW(20) },
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'center', md: 'flex-start' },
    width: { xs: '95%', md: 'unset' },
  },
  contentBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(15), md: pxToRem(15), lg: pxTovW(15) },
    backgroundColor: 'white',
    width: { xs: '95%', md: pxToRem(540), lg: pxTovW(597) },
    borderRadius: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
    paddingX: { xs: pxToRem(10), md: pxToRem(24), lg: pxTovW(30) },
    paddingY: { xs: pxToRem(10), md: pxToRem(20), lg: pxTovW(23) },
    maxHeight: { xs: '85vh', md: '90vh' },
    overflowY: 'hidden',
    boxSizing: 'border-box',
  },
  classAndSectionContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'column' },
    gap: { xs: pxToRem(15), md: pxToRem(21), lg: pxTovW(25) },
    width: '100%',
    flex: 1,
    minHeight: 0,
  },
  proceedButton: {
    width: { xs: '50%', md: pxToRem(150), lg: pxTovW(200) },
    height: { xs: pxToRem(35), md: 'unset' },
    fontWeight: 'bold',
    paddingY: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
  },
};

export interface IProjectStudentMarksInput {
  id: string;
  name: string;
  classsection: string;
  rollNo?: string;
  totalMarks: string;
  marks: string;
}

interface IProps {
  popupOpen: boolean;
  popupCloseHandler: () => void;
  proceedButtonData: {
    onClick: (marks: { studentId: string; obtainedMarks: number }[]) => void;
    disabled: boolean;
  };
  groupName?: string;
  /** Falls back to sample students when the caller has no group loaded. */
  studentList?: IProjectStudentMarksInput[];
}

export const ProjectIndividualMarksDialog = (props: IProps) => {
  const {
    popupOpen,
    popupCloseHandler,
    proceedButtonData,
    groupName,
    studentList: StudentListFromProps,
  } = props;

  const studentList = StudentListFromProps || [];

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [students, setStudents] =
    useState<IProjectStudentMarksInput[]>(studentList);

  /** Reopening for another group must start from that group's own marks. */
  useEffect(() => {
    if (popupOpen) {
      setStudents(studentList);
    }
  }, [popupOpen, studentList]);

  const handleMarksChange = (id: string, marks: string) => {
    if (!/^\d*$/.test(marks)) return;
    setStudents((prev) =>
      prev.map((student) =>
        student.id === id &&
          (marks === '' || Number(marks) <= Number(student.totalMarks))
          ? { ...student, marks }
          : student
      )
    );
  };

  const enteredMarks = students
    .filter((student) => student.marks !== '')
    .map((student) => ({
      studentId: student.id,
      obtainedMarks: Number(student.marks),
    }));
  return (
    <Modal open={popupOpen} onClose={popupCloseHandler}>
      <Slide
        direction="up"
        in={popupOpen}
        mountOnEnter
        unmountOnExit
        timeout={500}
      >
        <Box sx={styles.root}>
          <Box sx={styles.container}>
            <Box sx={styles.contentBox}>
              <Typography
                variant={'bodyText'}
                fontWeight="bold"
                textAlign={'center'}
              >
                Give Marks
              </Typography>
              <Box sx={styles.classAndSectionContainer}>
                <Box
                  sx={{
                    height: { xs: pxToRem(102), md: pxToRem(78), lg: pxTovW(119) },
                    width: { xs: '100%', md: pxToRem(480), lg: pxTovW(537) },
                    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
                    backgroundColor: '#EFF4EF',
                    border: '1px solid #DBDBDB',
                    padding: {
                      xs: `${pxToRem(12)} ${pxToRem(12)} ${pxToRem(
                        12
                      )} ${pxToRem(12)}`,
                      md: pxToRem(14),
                      lg: `${pxTovW(24)} ${pxTovW(17)}`,
                    },
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: pxToRem(10), md: pxToRem(21), lg: pxTovW(24) },
                    boxSizing: 'border-box',
                  }}
                >
                  <IconWrapper
                    name="gil-group"
                    parentFolder="icons"
                    type="png"
                    size="small"
                    customSx={{
                      height: {
                        xs: pxToRem(46),
                        md: pxToRem(34),
                        lg: pxTovW(46),
                      },
                      width: {
                        xs: pxToRem(46),
                        md: pxToRem(34),
                        lg: pxTovW(46),
                      },
                      cursor: 'pointer',
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: { xs: pxToRem(12), md: pxToRem(18), lg: pxTovW(20) },
                    }}
                  >
                    {/* Left Section */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: pxToRem(4), md: pxToRem(6), lg: pxTovW(6) },
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {groupName || 'AI Innovators'}
                      </Typography>

                      <Typography variant="subText" color={'#9F9F9F'}>
                        All members will receive the same marks
                      </Typography>
                    </Box>

                    {/* Right Section */}
                    {/* <Box
                                            sx={{
                                                width: { xs: pxToRem(76), md: pxToRem(62), lg: pxTovW(92) },
                                                height: { xs: pxToRem(56), md: pxToRem(42), lg: pxTovW(60) },
                                                bgcolor: "#FFFFFF",
                                                border: "1px solid #DBDBDB",
                                                borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                gap: { xs: pxToRem(5), md: pxToRem(3), lg: pxTovW(3) },
                                                p: { xs: pxToRem(8), md: pxToRem(13), lg: pxTovW(13) },
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Typography
                                                variant='subText'
                                                sx={{
                                                    color: "#9F9F9F",
                                                    fontWeight: 700
                                                }}
                                            >
                                                Enter Marks
                                            </Typography>
                                            <Divider
                                                sx={{
                                                    my: 0.1,
                                                    borderColor: "#E0E0E0",
                                                }}
                                            />
                                            <Typography
                                                variant='subText'
                                                sx={{
                                                    textAlign: "right",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                /100
                                            </Typography>
                                        </Box> */}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="bodyText" fontWeight={700}>
                    Students ({students.length})
                  </Typography>
                  {/* <Typography sx={{
                                        fontSize: { xs: pxToRem(15), md: pxToRem(15), lg: pxTovW(15) },
                                        fontWeight: 400,
                                        color: '#B3B3B3',
                                        textDecoration: 'underline',
                                        textUnderlineOffset: '3px',
                                    }}>Individual</Typography> */}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    maxWidth: { md: pxToRem(480), lg: pxTovW(537) },
                    gap: { xs: pxToRem(8), md: pxToRem(8), lg: pxTovW(8) },
                    flex: 1,
                    minHeight: 0,
                    marginY: { md: pxToRem(3), lg: pxTovW(3) },
                    overflowY: 'auto',
                    backgroundColor: '#ffffff',
                    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },

                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#FFFFFF',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#C5C5C5',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-corner': {
                      background: '#FFFFFF', // Removes the grey corner
                    },

                    scrollbarWidth: 'thin',
                    scrollbarColor: '#C5C5C5 #FFFFFF',
                  }}
                >
                  {students.map((student) => (
                    <StudentMarksCard
                      key={student.id}
                      name={student.name}
                      classsection={student.classsection}
                      rollNo={student.rollNo}
                      totalMarks={student.totalMarks}
                      marks={student.marks}
                      onMarksChange={(value) =>
                        handleMarksChange(student.id, value)
                      }
                    />
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  height: { xs: pxToRem(50), md: pxToRem(36), lg: pxTovW(50) },
                  width: { xs: '100%', md: pxToRem(480), lg: pxTovW(537) },
                  borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
                  backgroundColor: '#EFF4EF',
                  border: '1px solid #DBDBDB',
                  padding: {
                    xs: `${pxToRem(12)} ${pxToRem(12)} ${pxToRem(12)} ${pxToRem(
                      12
                    )}`,
                    md: pxToRem(14),
                    lg: `${pxTovW(13)} ${pxTovW(28)}`,
                  },
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: pxToRem(10), md: pxToRem(21), lg: pxTovW(24) },
                  boxSizing: 'border-box',
                }}
              >
                <IconWrapper
                  name="gil-notice"
                  parentFolder="icons"
                  type="png"
                  size="small"
                  customSx={{
                    height: {
                      xs: pxToRem(24),
                      md: pxToRem(20),
                      lg: pxTovW(24),
                    },
                    width: {
                      xs: pxToRem(24),
                      md: pxToRem(20),
                      lg: pxTovW(24),
                    },
                  }}
                />
                <Typography variant="h5" fontWeight={'bold'} color={'#B3B3B3'}>
                  Marks are assigned individually to each of the{' '}
                  {students.length} members
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: { xs: 'center', lg: 'flex-end' },
                  gap: { xs: pxToRem(15), md: pxToRem(18), lg: pxTovW(20) },
                }}
              >
                <Button
                  variant="contained"
                  onClick={popupCloseHandler}
                  sx={{
                    width: {
                      xs: '50%',
                      md: pxToRem(150),
                      lg: pxTovW(200),
                    },
                    height: { xs: pxToRem(52), lg: 'unset' },
                    fontFamily: 'Lato',
                    fontWeight: 700,
                    color: '#0AA34F',
                    border: '1px solid #0AA34F',
                    fontSize: { xs: pxToRem(16), md: pxToRem(16), lg: pxTovW(16) },
                    borderRadius: '10px',
                    backgroundColor: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#FFFFFF',
                      color: '#0AA34F',
                      border: '1px solid #0AA34F',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Cancel
                </Button>
                <PrimaryButton
                  variant="contained"
                  onClick={() => proceedButtonData.onClick(enteredMarks)}
                  sx={styles.proceedButton}
                  disabled={
                    proceedButtonData.disabled || enteredMarks.length === 0
                  }
                >
                  <Typography variant="cardText" fontWeight={700}>
                    Submit Marks
                  </Typography>
                </PrimaryButton>
              </Box>
            </Box>
            <Button onClick={popupCloseHandler} sx={{ p: '0px' }}>
              <IconWrapper
                name="close-yellow"
                size="md"
                parentFolder="icons"
                type="png"
                customSx={{
                  width: { xs: pxToRem(33), md: pxToRem(39), lg: pxTovW(54) },
                  height: { xs: pxToRem(33), md: pxToRem(39), lg: pxTovW(54) },
                }}
              />
            </Button>
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
};
interface StudentProps {
  name: string;
  classsection: string;
  rollNo?: string;
  totalMarks: string;
  marks: string;
  onMarksChange: (value: string) => void;
}
const StudentMarksCard = (props: StudentProps) => {
  const { name, classsection, rollNo, totalMarks, marks, onMarksChange } =
    props;
  return (
    <Box
      sx={{
        height: { xs: pxToRem(102), md: pxToRem(78), lg: pxTovW(119) },
        width: { xs: '100%', md: '100%' },
        borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
        backgroundColor: '#FFFFFF',
        border: '1px solid #E0DFDE',
        padding: {
          xs: `${pxToRem(12)} ${pxToRem(12)} ${pxToRem(12)} ${pxToRem(12)}`,
          md: pxToRem(14),
          lg: `${pxTovW(24)} ${pxTovW(17)}`,
        },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
        boxSizing: 'border-box',
        boxShadow: '0px 0px ${pxToRem(4)} rgba(0, 0, 0, 0.08)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
          height: { xs: '100%', md: '100%', lg: pxTovW(56) },
          width: { xs: pxToRem(60), md: pxToRem(48), lg: pxTovW(58) },
          minWidth: { xs: pxToRem(60), md: pxToRem(48), lg: pxTovW(58) },
          boxSizing: 'border-box',
          borderRadius: { xs: pxToRem(16), md: pxToRem(16), lg: pxTovW(16) },
          backgroundColor: '#E0DFDE',
          position: 'relative',
        }}
      >
        <ImageWrapper
          name="student1"
          type="png"
          parentFolder="tempAssets"
          styles={{
            width: '100%',
            objectFit: 'cover',
            borderRadius: { xs: pxToRem(16), md: pxToRem(16), lg: pxTovW(16) },
            height: '100%',
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: { xs: pxToRem(12), md: pxToRem(18), lg: pxTovW(20) },
        }}
      >
        {/* Left Section */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: pxToRem(4), md: pxToRem(6), lg: pxTovW(6) },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
            }}
          >
            {name}
          </Typography>

          <Typography variant="subText" color={'#9F9F9F'}>
            {classsection}
            {rollNo ? ` | RollNo : ${rollNo}` : ''}
          </Typography>
        </Box>

        {/* Right Section */}
        <Box
          sx={{
            width: { xs: pxToRem(76), md: pxToRem(51), lg: pxTovW(75) },
            height: { xs: pxToRem(56), md: pxToRem(42), lg: pxTovW(60) },
            bgcolor: '#FFFFFF',
            border: '1px solid #DBDBDB',
            borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: { xs: pxToRem(5), md: pxToRem(3), lg: pxTovW(3) },
            p: { xs: pxToRem(8), md: pxToRem(13), lg: pxTovW(13) },
            flexShrink: 0,
          }}
        >
          <InputBase
            value={marks}
            onChange={(e) => onMarksChange(e.target.value)}
            placeholder="Enter Marks"
            inputProps={{
              inputMode: 'numeric',
              maxLength: 3,
            }}
            sx={{
              color: '#9F9F9F',
              fontWeight: 700,
              fontSize: { md: pxToRem(12), lg: pxTovW(12) },
              width: '100%',
              '& input::placeholder': {
                color: '#9F9F9F',
                opacity: 1,
                fontWeight: 700,
              },
            }}
          />
          <Divider
            sx={{
              my: 0.1,
              borderColor: '#E0E0E0',
            }}
          />
          <Typography
            variant="subText"
            sx={{
              textAlign: 'right',
              fontWeight: 700,
            }}
          >
            /{totalMarks}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectIndividualMarksDialog;
